import { getApiConfig } from "../config";
import { proxyFetch } from "./proxyFetch";
import type { WheelRecommendation } from "../ui/watchlistData";

const BATCH_PATH = "/marketdata/calls-batch";

function marketdataBatchUrl(): string | null {
  const { baseUrl } = getApiConfig();
  if (!baseUrl) return null;
  if (baseUrl === window.location.origin) return BATCH_PATH;
  return `${baseUrl}${BATCH_PATH}`;
}

export interface MarketdataPositionRequest {
  ticker: string;
  avgCost: number;
  currentPrice?: number;
}

export interface MarketdataBatchResult {
  ok: boolean;
  recommendations: WheelRecommendation[];
  creditsUsed: number;
  tickersFetched: number;
  dailyCreditsRemaining?: number;
  code?: string;
  message?: string;
  perTicker?: Array<{ ticker?: string; cached?: boolean; ok?: boolean }>;
}

function marketdataHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

/** Fetch below-avg call chains only for the blocked positions the client sends. */
export async function fetchMarketdataCallsBatch(
  positions: MarketdataPositionRequest[],
  force = false,
): Promise<MarketdataBatchResult> {
  const empty: MarketdataBatchResult = {
    ok: false,
    recommendations: [],
    creditsUsed: 0,
    tickersFetched: 0,
  };

  const batchUrl = marketdataBatchUrl();
  if (!batchUrl || !positions.length) {
    return { ...empty, code: "proxy_unconfigured" };
  }

  try {
    const res = await proxyFetch(batchUrl, {
      method: "POST",
      headers: marketdataHeaders(),
      body: JSON.stringify({ positions, force }),
      cache: "no-store",
    });

    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ...empty,
        code: String(data.code ?? "http_error"),
        message: String(data.message ?? `${res.status} ${res.statusText}`),
      };
    }

    return {
      ok: Boolean(data.ok),
      recommendations: Array.isArray(data.recommendations)
        ? (data.recommendations as WheelRecommendation[])
        : [],
      creditsUsed: Number(data.creditsUsed) || 0,
      tickersFetched: Number(data.tickersFetched) || 0,
      dailyCreditsRemaining:
        data.dailyCreditsRemaining != null ? Number(data.dailyCreditsRemaining) : undefined,
      code: data.code != null ? String(data.code) : undefined,
      message: data.message != null ? String(data.message) : undefined,
      perTicker: Array.isArray(data.perTicker)
        ? (data.perTicker as MarketdataBatchResult["perTicker"])
        : undefined,
    };
  } catch (err) {
    return {
      ...empty,
      code: "network_error",
      message: err instanceof Error ? err.message : "fetch failed",
    };
  }
}
